// Secciones del cuerpo de un editor con ciclo de vida, con los mismos títulos y orden en todos:
// Descripción · Palancas arriba; luego A quién le aplica, Alcance, Cuándo se paga, Reglas, etc.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { EditorSection, SECTION } from '@/shared/patterns/EditorForm';
import { LIFECYCLE_SECTION_HELP } from './helpText';

export type LifecycleSectionKey = keyof typeof LIFECYCLE_SECTION_HELP;

export const LIFECYCLE_SECTION_TITLE: Record<LifecycleSectionKey, string> = {
    description: 'Descripción',
    levers: 'Palancas',
    audience: 'A quién le aplica',
    scope: 'Alcance',
    whenPaid: 'Cuándo se paga',
    rules: 'Reglas',
    budget: 'Presupuesto',
    validity: 'Vigencia',
};

const TOP: readonly LifecycleSectionKey[] = ['description', 'levers'];
const BOTTOM: readonly LifecycleSectionKey[] = [
    'audience',
    'scope',
    'whenPaid',
    'rules',
    'budget',
    'validity',
];

/** Proporciones de la fila superior: la columna de palancas es la ancha. */
const TOP_COLUMNS: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-[minmax(0,0.8fr)_minmax(0,1.65fr)]',
};

export type LifecycleEditorLayoutProps = Partial<Record<LifecycleSectionKey, ReactNode>> & {
    /** Lo elegido en una línea, a la derecha del título de la sección. */
    summaries?: Partial<Record<LifecycleSectionKey, ReactNode>>;
    /** Ayuda propia del dominio cuando la común no basta. */
    help?: Partial<Record<LifecycleSectionKey, string>>;
};

export function LifecycleEditorLayout({
    summaries = {},
    help = {},
    ...slots
}: LifecycleEditorLayoutProps) {
    const present = (keys: readonly LifecycleSectionKey[]) =>
        keys.filter(
            (key) => slots[key] !== undefined && slots[key] !== null && slots[key] !== false,
        );
    const section = (key: LifecycleSectionKey, bare: boolean) => (
        <EditorSection
            key={key}
            title={LIFECYCLE_SECTION_TITLE[key]}
            help={help[key] ?? LIFECYCLE_SECTION_HELP[key]}
            summary={summaries[key]}
            bare={bare}
        >
            {slots[key]}
        </EditorSection>
    );
    const top = present(TOP);
    return (
        <>
            {top.length > 0 ? (
                <div className={cn(SECTION, 'grid gap-4', TOP_COLUMNS[top.length])}>
                    {top.map((key) => section(key, true))}
                </div>
            ) : null}
            {present(BOTTOM).map((key) => section(key, false))}
        </>
    );
}
